import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { DECK_THEME as T } from "../../lib/deckModel";
import { SectionContent, SectionHeader } from "./DeckSections";
import { OverviewPage } from "./OverviewPage";

export function DeckPresenter({ deck, model, onClose, start = 0 }) {
  const sections = (deck.sections || []).filter((s) => !s.hidden);
  const total = Math.max(1, sections.length);
  const [i, setI] = useState(Math.max(0, start - 1));
  const [dir, setDir] = useState(1);
  const go = useCallback((d) => setI((x) => { const nx = Math.min(total - 1, Math.max(0, x + d)); setDir(d); return nx; }), [total]);

  useEffect(() => {
    const el = document.documentElement;
    if (el.requestFullscreen && !document.fullscreenElement) el.requestFullscreen().catch(() => {});
    const onKey = (e) => {
      if (e.key !== "Escape" && (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.target.closest?.("[role=slider],[role=combobox],[role=listbox],[role=tablist]"))) return;
      if (["ArrowRight", "ArrowDown", "PageDown", " "].includes(e.key)) { e.preventDefault(); go(1); }
      if (["ArrowLeft", "ArrowUp", "PageUp"].includes(e.key)) { e.preventDefault(); go(-1); }
      if (e.key === "Escape") onClose();
    };
    const onFs = () => { if (!document.fullscreenElement) onClose(); };
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFs);
      document.body.style.overflow = "";
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, [go, onClose]);

  const s = sections[i];
  return (
    <div className="fixed inset-0 z-[100] flex flex-col" style={{ background: T.warm }} data-testid="deck-presenter">
      <div className="h-1 w-full" style={{ background: "rgba(127,127,127,0.2)" }}>
        <div className="h-1 transition-[width] duration-500" style={{ width: `${((i + 1) / total) * 100}%`, background: T.accent }} data-testid="deck-presenter-progress" />
      </div>
      <div className="flex-1 overflow-y-auto relative">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div key={i} custom={dir} initial={{ opacity: 0, x: dir * 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: dir * -60 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="min-h-full">
            {!s ? null : s.type === "investment_overview" ? <OverviewPage deck={deck} section={s} model={model} set={() => {}} /> : (
              <div className="px-6 sm:px-12 lg:px-[8%] py-12 sm:py-16" style={{ fontFamily: T.sans }} data-testid={`deck-slide-${s.type}`}>
                <SectionHeader section={s} index={i} />
                <SectionContent section={s} deck={deck} model={model} />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex items-center justify-between gap-3 px-4 sm:px-8 py-3 border-t" style={{ borderColor: T.line, color: T.navy }}>
        <button onClick={onClose} style={{ fontFamily: T.mono }} className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] opacity-70 hover:opacity-100 transition-opacity" data-testid="deck-presenter-exit"><X className="h-4 w-4" />Exit</button>
        <div className="text-xs tabular-nums opacity-70" data-testid="deck-presenter-counter">{i + 1} / {total}</div>
        <div className="flex gap-2">
          <button onClick={() => go(-1)} disabled={i === 0} className="h-10 w-10 rounded-full border flex items-center justify-center disabled:opacity-30 hover:scale-105 transition-transform" style={{ borderColor: "currentColor" }} data-testid="deck-presenter-prev"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={() => go(1)} disabled={i === total - 1} className="h-10 w-10 rounded-full flex items-center justify-center text-white disabled:opacity-30 hover:scale-105 transition-transform" style={{ background: T.accent }} data-testid="deck-presenter-next"><ChevronRight className="h-5 w-5" /></button>
        </div>
      </div>
    </div>
  );
}
